import React, { useEffect, useMemo, useState } from "react"
import { Order } from "../model/OldOrderModel"
import orderReportService from "../services/oldService"
import { collection, doc, setDoc, Timestamp } from "firebase/firestore"
import { db } from "../firebase/firebase"

// Restaurant IDs
const RESTAURANTS = [
  { id: "5Pnrh7VZaUdT5L9yk8QRMnetGHq2", name: "Restaurant 1" },
  { id: "M32JVdmxy2MgKjDSB6MrS40LLHn2", name: "Restaurant 2" },
  { id: "S3jQwlmqV0gsNeoWDmEafan2X5o2", name: "Restaurant 3" },
  { id: "TAn9niOIPlcHXZTkWq7B0cI8atF3", name: "Restaurant 4" },
  { id: "luA79ZzSNTSeuTpIaknPpWKvPSw2", name: "Restaurant 5" },
  { id: "qY5OolFYj1USkfpw5tjf0BmDjR33", name: "Restaurant 6" },
  { id: "sSLeuSK3VEbkB5At3mGGaYgnCmI3", name: "Restaurant 7" },
  { id: "voT4WYa4VNMQnYgFXlKVcIUeuEL2", name: "Restaurant 8" }
]

// Delivery Partner IDs
const DELIVERY_PARTNERS = [
  { id: "2ubDZewv6AZY7XsthRTdx2ywP0C3", name: "DP 1" },
  { id: "7VVSwOlMkYaUoEM9CY9SlnTYq0U2", name: "DP 2" },
  { id: "7lbckfSmoWMp9UCQBH8cvsPOQCV2", name: "DP 3" },
  { id: "8u5luLlgrIdMi9H5ig6UOYCH1t63", name: "DP 4" },
  { id: "FOzvOebqUjhpkD3yuN737oHpkGJ2", name: "DP 5" },
  { id: "JmfWlsgH3JZATCzdtstsevipOdJ2", name: "DP 6" },
  { id: "nUei7qKbVlh2KhLugmk5noUoba52", name: "DP 7" },
  { id: "qEHDAyzEduT7K2w0gsN8CGcuqCN2", name: "DP 8" },
  { id: "voT4WYa4VNMQnYgFXlKVcIUeuEL2", name: "DP 9" },
  { id: "whLlB3ABnjXTIGbsDzj1VbgQLXJ3", name: "DP 10" },
  { id: "x8lpoLh61ic4YsUrMwxr5bF93O03", name: "DP 11" }
]

interface OrderReportPageProps {
  branchId?: string;
}

const OrderReportPage: React.FC<OrderReportPageProps> = ({ branchId = '' }) => {

  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [restaurantFilter, setRestaurantFilter] = useState<string>("all")
  const [dpFilter, setDpFilter] = useState<string>("all")

  const [month, setMonth] = useState<number>(
    new Date().getMonth() + 1
  )
  const [year, setYear] = useState<number>(
    new Date().getFullYear()
  )
  const [allOrdersCount, setAllOrdersCount] = useState<number>(0)
  const [migrating, setMigrating] = useState(false)
  const [migrateResult, setMigrateResult] = useState<{ success: number; skipped: number } | null>(null)
  const [migrateProgress, setMigrateProgress] = useState({ done: 0, total: 0 })

  const migrateToNewOrders = async () => {
    if (filteredOrders.length === 0) return
    const confirmed = window.confirm(
      `Migrate ${filteredOrders.length} filtered orders to the new Orders collection?\n\nExisting orders with the same ID will be skipped.`
    )
    if (!confirmed) return

    const total = filteredOrders.length
    setMigrating(true)
    setMigrateResult(null)
    setMigrateProgress({ done: 0, total })
    let success = 0
    let skipped = 0

    for (const o of filteredOrders) {
      try {
        const docId = (o as any).id || o.orderNumber || `migrated_${Date.now()}_${Math.random().toString(36).slice(2)}`

        // Convert old format → new OrderModel format
        let createdAt: number
        if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
          createdAt = (o.orderPlaceDate as any).toDate().getTime()
        } else {
          createdAt = typeof o.orderPlaceDate === 'number' ? o.orderPlaceDate : Date.now()
        }

        const newOrder = {
          id: docId,
          active: o.active ?? false,
          branchId: o.xone || '',
          customerId: o.uid || '',
          status: (o.status || 'PENDING').toUpperCase(),
          totalPrice: parseFloat(o.totalPrice) || 0,
          distance: o.distance || 0,
          createdAt,
          orderNumber: [o.orderNumber],
          charges: {
            deliveryCharge: parseFloat(o.deliveryCharge) || 0,
            packing: parseFloat(o.packing) || 0,
            service: parseFloat(o.service) || 0,
            tax: parseFloat(o.tax) || 0,
          },
          userLocation: {
            address: o.customerAddress || '',
            contact: o.customerNumber || '',
            latitude: parseFloat(o.lattitude) || 0,
            longitude: parseFloat(o.longitude) || 0,
            name: o.customerName || '',
            secondaryContact: o.customerNumber || '',
          },
          sellerLocation: {
            address: '',
            contact: '',
            latitude: parseFloat(o.storeLat) || 0,
            longitude: parseFloat(o.storeLon) || 0,
            name: o.customerStore || '',
            secondaryContact: '',
          },
          payment: {
            amount: parseFloat(o.totalPrice) || 0,
            method: o.payment || '',
            paymentId: '',
            status: o.paymentStatus || '',
            sellerId: o.customerStoreUid || '',
          },
          orderItems: [],
          deliveryPartnerId: o.dpId || null,
          migratedFrom: 'orders_legacy',
          migratedAt: Date.now(),
        }

        await setDoc(doc(db, 'Orders', docId), newOrder, { merge: false })
        success++
      } catch (err: any) {
        // Skip duplicates or errors
        skipped++
        console.warn('Migration skipped order:', o.orderNumber, err?.message)
      }
      setMigrateProgress({ done: success + skipped, total })
    }

    setMigrating(false)
    setMigrateResult({ success, skipped })
    window.alert(`Migration complete!\n✅ Migrated: ${success}\n⚠️ Skipped: ${skipped}`)
  }

  /* ===============================
     FETCH ALL ORDERS
  ================================ */
  useEffect(() => {
    const loadOrders = async () => {
      console.log("🚀 OrderReportPage: Starting to load orders...")
      console.log("🏪 Using Branch ID:", branchId)
      
      setLoading(true)
      setDebugInfo(null)
      
      try {
        // Fetch all orders without branch filter
        const data = await orderReportService.fetchAllOrders(branchId || undefined)
        console.log("✅ Orders loaded successfully:", data.length, "orders")
        setOrders(data)
        setAllOrdersCount(data.length)
        
        // Set debug info
        if (data.length === 0) {
          setDebugInfo({
            type: 'no_orders',
            branchId: branchId,
            message: 'No orders found for this branch'
          })
        }
      } catch (error) {
        console.error("❌ Error loading orders:", error)
        setDebugInfo({
          type: 'error',
          error: error,
          message: 'Failed to load orders from Firestore'
        })
      } finally {
        setLoading(false)
        console.log("🏁 Loading complete")
      }
    }

    loadOrders()
  }, [branchId])

  /* ===============================
     FILTER ORDERS
  ================================ */
  const filteredOrders = useMemo(() => {
    console.log(`🗓️ Filtering orders for ${new Date(0, month - 1).toLocaleString("default", { month: "long" })} ${year}`)
    console.log("📊 Total orders before filter:", orders.length)
    
    let filtered = orderReportService.filterMonthlyOrders(orders, month, year)
    
    console.log("✅ Orders after monthly filter:", filtered.length)
    
    // Apply status filter
    if (statusFilter !== "all") {
      const beforeStatusFilter = filtered.length
      filtered = filtered.filter(order => {
        const status = order.status?.toLowerCase()
        
        switch (statusFilter) {
          case "delivered":
            return status === "delivered" || status === "completed"
          case "pending":
            return status === "pending" || status === "processing" || status === "confirmed"
          case "cancelled":
            return status === "cancelled" || status === "canceled"
          default:
            return true
        }
      })
      console.log(`🔍 Status filter "${statusFilter}": ${beforeStatusFilter} → ${filtered.length} orders`)
    }

    // Apply restaurant filter
    if (restaurantFilter !== "all") {
      const beforeRestaurantFilter = filtered.length
      filtered = filtered.filter(order => order.customerStoreUid === restaurantFilter)
      console.log(`🏪 Restaurant filter: ${beforeRestaurantFilter} → ${filtered.length} orders`)
    }

    // Apply DP filter
    if (dpFilter !== "all") {
      const beforeDpFilter = filtered.length
      filtered = filtered.filter(order => order.dpId === dpFilter)
      console.log(`🚚 DP filter: ${beforeDpFilter} → ${filtered.length} orders`)
    }
    
    if (filtered.length > 0) {
      console.log("📅 Date range of filtered orders:")
      const dates = filtered.map(o => {
        if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
          return (o.orderPlaceDate as any).toDate()
        }
        return new Date(o.orderPlaceDate)
      })
      console.log("  Earliest:", new Date(Math.min(...dates.map(d => d.getTime()))).toLocaleDateString())
      console.log("  Latest:", new Date(Math.max(...dates.map(d => d.getTime()))).toLocaleDateString())
    } else if (orders.length > 0) {
      // Show available months
      const months = new Set(orders.map(o => {
        let d: Date
        if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
          d = (o.orderPlaceDate as any).toDate()
        } else {
          d = new Date(o.orderPlaceDate)
        }
        return `${d.getFullYear()}-${d.getMonth() + 1}`
      }))
      console.log("📆 Available months with orders:", Array.from(months).sort())
    }
    
    return filtered
  }, [orders, month, year, statusFilter, restaurantFilter, dpFilter])

  /* ===============================
     GET AVAILABLE MONTHS
  ================================ */
  const availableMonths = useMemo(() => {
    const monthSet = new Map<string, { month: number, year: number, count: number }>()
    
    orders.forEach(o => {
      // Handle both Firestore Timestamp and number
      let orderDate: Date
      
      if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
        orderDate = (o.orderPlaceDate as any).toDate()
      } else if (typeof o.orderPlaceDate === 'number') {
        orderDate = new Date(o.orderPlaceDate)
      } else {
        console.warn("Invalid date format for order:", o.orderNumber)
        return
      }
      
      const key = `${orderDate.getFullYear()}-${orderDate.getMonth() + 1}`
      const existing = monthSet.get(key)
      
      if (existing) {
        existing.count++
      } else {
        monthSet.set(key, {
          month: orderDate.getMonth() + 1,
          year: orderDate.getFullYear(),
          count: 1
        })
      }
    })
    
    return Array.from(monthSet.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year
      return b.month - a.month
    })
  }, [orders])

  /* ===============================
     NAVIGATION
  ================================ */
  const goToPreviousMonth = () => {
    if (month === 1) {
      setMonth(12)
      setYear(year - 1)
    } else {
      setMonth(month - 1)
    }
  }

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1)
      setYear(year + 1)
    } else {
      setMonth(month + 1)
    }
  }

  /* ===============================
     EXPORT EXCEL
  ================================ */
  const exportExcel = () => {
    orderReportService.exportToExcel(filteredOrders, month, year)
  }

  /* ===============================
     CALCULATE TOTALS
  ================================ */
  const totalRevenue = useMemo(() => {
    return filteredOrders.reduce((sum, order) => {
      return sum + (parseFloat(order.totalPrice) || 0)
    }, 0)
  }, [filteredOrders])

  const statusCounts = useMemo(() => {
    const monthlyOrders = orderReportService.filterMonthlyOrders(orders, month, year)
    
    return {
      all: monthlyOrders.length,
      delivered: monthlyOrders.filter(o => {
        const status = o.status?.toLowerCase()
        return status === "delivered" || status === "completed"
      }).length,
      pending: monthlyOrders.filter(o => {
        const status = o.status?.toLowerCase()
        return status === "pending" || status === "processing" || status === "confirmed"
      }).length,
      cancelled: monthlyOrders.filter(o => {
        const status = o.status?.toLowerCase()
        return status === "cancelled" || status === "canceled"
      }).length
    }
  }, [orders, month, year])

  // Get unique restaurants and DPs from current month's orders
  const availableRestaurants = useMemo(() => {
    const monthlyOrders = orderReportService.filterMonthlyOrders(orders, month, year)
    const uniqueIds = new Set(monthlyOrders.map(o => o.customerStoreUid))
    return RESTAURANTS.filter(r => uniqueIds.has(r.id))
  }, [orders, month, year])

  const availableDPs = useMemo(() => {
    const monthlyOrders = orderReportService.filterMonthlyOrders(orders, month, year)
    const uniqueIds = new Set(monthlyOrders.map(o => o.dpId).filter(Boolean))
    return DELIVERY_PARTNERS.filter(dp => uniqueIds.has(dp.id))
  }, [orders, month, year])

  return (
    <div style={styles.container}>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📦 Monthly Orders Report</h1>
        <p style={styles.subtitle}>Track and manage your monthly order performance</p>
      </div>

      {/* Debug Info Banner */}
      {!loading && debugInfo && (
        <div style={styles.debugBanner}>
          <div style={styles.debugHeader}>
            <span style={styles.debugIcon}>🔍</span>
            <h3 style={styles.debugTitle}>Debug Information</h3>
          </div>
          <div style={styles.debugContent}>
            {debugInfo.type === 'no_orders' && (
              <>
                <p style={styles.debugText}>
                  <strong>❌ No orders found</strong>
                </p>
                <p style={styles.debugText}>
                  <code style={styles.code}>{debugInfo.message}</code>
                </p>
              </>
            )}
            {debugInfo.type === 'error' && (
              <>
                <p style={styles.debugText}>
                  <strong>❌ Error loading orders</strong>
                </p>
                <p style={styles.debugText}>
                  {debugInfo.message}
                </p>
                <p style={styles.debugText}>
                  <code style={styles.code}>{String(debugInfo.error)}</code>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Orders Found Info */}
      {!loading && orders.length > 0 && (
        <div style={styles.successBanner}>
          <span style={styles.successIcon}>✅</span>
          <div style={{ flex: 1 }}>
            <strong>Orders loaded successfully!</strong>
            <p style={styles.successText}>
              Found {orders.length} total orders
            </p>
            {filteredOrders.length === 0 && availableMonths.length > 0 && (
              <div style={styles.monthSuggestions}>
                <strong>💡 Available months with orders:</strong>
                <div style={styles.monthBadges}>
                  {availableMonths.slice(0, 6).map((m, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setMonth(m.month)
                        setYear(m.year)
                      }}
                      style={styles.monthBadge}
                    >
                      {new Date(0, m.month - 1).toLocaleString("default", { month: "short" })} {m.year}
                      <span style={styles.monthCount}>({m.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters & Navigation */}
      <div style={styles.filterCard}>
        <div style={styles.navigation}>
          <button
            onClick={goToPreviousMonth}
            style={styles.navBtn}
            title="Previous Month"
          >
            ← Previous
          </button>

          <div style={styles.monthDisplay}>
            <span style={styles.monthText}>
              {new Date(0, month - 1).toLocaleString("default", { month: "long" })} {year}
            </span>
          </div>

          <button
            onClick={goToNextMonth}
            style={styles.navBtn}
            title="Next Month"
          >
            Next →
          </button>
        </div>

        <div style={styles.filters}>
          <div style={styles.selectGroup}>
            <label style={styles.label}>Month</label>
            <select
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
              style={styles.select}
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.selectGroup}>
            <label style={styles.label}>Year</label>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              style={styles.select}
            >
              {[2023, 2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div style={styles.selectGroup}>
            <label style={styles.label}>Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All ({statusCounts.all})</option>
              <option value="delivered">Delivered ({statusCounts.delivered})</option>
              <option value="pending">Pending ({statusCounts.pending})</option>
              <option value="cancelled">Cancelled ({statusCounts.cancelled})</option>
            </select>
          </div>

          <div style={styles.selectGroup}>
            <label style={styles.label}>Restaurant</label>
            <select
              value={restaurantFilter}
              onChange={e => setRestaurantFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Restaurants</option>
              {availableRestaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div style={styles.selectGroup}>
            <label style={styles.label}>Delivery Partner</label>
            <select
              value={dpFilter}
              onChange={e => setDpFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All DPs</option>
              {availableDPs.map(dp => (
                <option key={dp.id} value={dp.id}>{dp.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={exportExcel}
            style={{
              ...styles.exportBtn,
              opacity: filteredOrders.length === 0 ? 0.5 : 1
            }}
            disabled={filteredOrders.length === 0}
          >
            <span style={styles.btnIcon}>📥</span>
            Export Excel
          </button>

          <button
            onClick={migrateToNewOrders}
            disabled={migrating || filteredOrders.length === 0}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: "none",
              backgroundColor: migrating ? '#9CA3AF' : '#7C3AED',
              color: "white",
              fontSize: "13px",
              fontWeight: "700",
              cursor: migrating || filteredOrders.length === 0 ? "not-allowed" : "pointer",
              opacity: filteredOrders.length === 0 ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(124,58,237,0.3)",
            }}
            title={`Migrate ${filteredOrders.length} filtered orders to the new Orders collection`}
          >
            <span>{migrating ? '⏳' : '🔄'}</span>
            {migrating
              ? `${migrateProgress.done} / ${migrateProgress.total}`
              : `Migrate to New (${filteredOrders.length})`}
          </button>

          {/* Progress bar shown during migration */}
          {migrating && migrateProgress.total > 0 && (
            <div style={{ width: "100%", marginTop: "8px" }}>
              <div style={{
                width: "100%",
                height: "6px",
                backgroundColor: "#E9D5FF",
                borderRadius: "3px",
                overflow: "hidden",
              }}>
                <div style={{
                  height: "100%",
                  width: `${Math.round((migrateProgress.done / migrateProgress.total) * 100)}%`,
                  backgroundColor: "#7C3AED",
                  borderRadius: "3px",
                  transition: "width 0.2s ease",
                }} />
              </div>
              <div style={{ fontSize: "11px", color: "#7C3AED", marginTop: "3px", textAlign: "right" }}>
                {Math.round((migrateProgress.done / migrateProgress.total) * 100)}%
              </div>
            </div>
          )}
        </div>

        {migrateResult && (
          <div style={{
            marginTop: "12px",
            padding: "10px 16px",
            borderRadius: "8px",
            backgroundColor: "#F0FDF4",
            border: "1px solid #BBF7D0",
            fontSize: "13px",
            color: "#065F46",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <span>✅</span>
            <span>
              Last migration: <strong>{migrateResult.success} migrated</strong>
              {migrateResult.skipped > 0 && `, ${migrateResult.skipped} skipped`}
            </span>
            <button
              onClick={() => setMigrateResult(null)}
              style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "12px" }}
            >✕</button>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading orders...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>Order List</h3>
            <div style={styles.tableHeaderRight}>
              <span style={styles.badge}>{filteredOrders.length} Orders</span>
              {statusFilter !== "all" && (
                <span style={styles.filterBadge}>
                  Status: {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </span>
              )}
              {restaurantFilter !== "all" && (
                <span style={styles.filterBadge}>
                  Restaurant: {RESTAURANTS.find(r => r.id === restaurantFilter)?.name}
                </span>
              )}
              {dpFilter !== "all" && (
                <span style={styles.filterBadge}>
                  DP: {DELIVERY_PARTNERS.find(dp => dp.id === dpFilter)?.name}
                </span>
              )}
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Order No</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Store</th>
                  <th style={styles.th}>DP ID</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Payment</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={9} style={styles.emptyState}>
                      <div style={styles.emptyIcon}>📭</div>
                      <div style={styles.emptyText}>
                        <strong>No orders found for {new Date(0, month - 1).toLocaleString("default", { month: "long" })} {year}</strong>
                      </div>
                      {orders.length > 0 && (
                        <div style={styles.emptyHint}>
                          You have {orders.length} total orders, but none matching the current filters.
                          <br />
                          {availableMonths.length > 0 && (
                            <>
                              Try: {availableMonths.slice(0, 3).map((m, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    setMonth(m.month)
                                    setYear(m.year)
                                  }}
                                  style={styles.quickSelectBtn}
                                >
                                  {new Date(0, m.month - 1).toLocaleString("default", { month: "short" })} {m.year}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}

                {filteredOrders.map((o, i) => (
                  <tr key={o.orderNumber} style={styles.tr}>
                    <td style={styles.td}>{i + 1}</td>
                    <td style={styles.td}>
                      <span style={styles.orderNo}>{o.orderNumber}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.customerInfo}>
                        <span style={styles.customerName}>{o.customerName}</span>
                        <span style={styles.customerPhone}>{o.customerNumber}</span>
                      </div>
                    </td>
                    <td style={styles.td}>{o.customerStore}</td>
                    <td style={styles.td}>
                      <span style={styles.dpBadge}>
                        {o.dpId ? DELIVERY_PARTNERS.find(dp => dp.id === o.dpId)?.name || 'Unknown' : 'N/A'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(o.status)}>
                        {o.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.paymentBadge}>{o.payment}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.price}>₹{parseFloat(o.totalPrice).toFixed(2)}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.date}>
                        {(() => {
                          let orderDate: Date
                          if (o.orderPlaceDate && typeof o.orderPlaceDate === 'object' && 'toDate' in o.orderPlaceDate) {
                            orderDate = (o.orderPlaceDate as any).toDate()
                          } else {
                            orderDate = new Date(o.orderPlaceDate)
                          }
                          return orderDate.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })
                        })()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer summary */}
      {!loading && filteredOrders.length > 0 && (
        <div style={styles.summaryCard}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryIcon}>📊</div>
            <div>
              <div style={styles.summaryLabel}>Total Orders</div>
              <div style={styles.summaryValue}>{filteredOrders.length}</div>
            </div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryIcon}>💰</div>
            <div>
              <div style={styles.summaryLabel}>Total Revenue</div>
              <div style={styles.summaryValue}>₹{totalRevenue.toFixed(2)}</div>
            </div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryIcon}>📈</div>
            <div>
              <div style={styles.summaryLabel}>Average Order Value</div>
              <div style={styles.summaryValue}>
                ₹{filteredOrders.length > 0 ? (totalRevenue / filteredOrders.length).toFixed(2) : "0.00"}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

/* ===============================
   HELPER FUNCTIONS
================================ */
const getStatusStyle = (status: string) => {
  const baseStyle = {
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px"
  }

  switch (status?.toLowerCase()) {
    case "completed":
    case "delivered":
      return { ...baseStyle, background: "#d4edda", color: "#155724" }
    case "pending":
    case "processing":
      return { ...baseStyle, background: "#fff3cd", color: "#856404" }
    case "cancelled":
      return { ...baseStyle, background: "#f8d7da", color: "#721c24" }
    case "confirmed":
      return { ...baseStyle, background: "#d1ecf1", color: "#0c5460" }
    default:
      return { ...baseStyle, background: "#e2e3e5", color: "#383d41" }
  }
}

/* ===============================
   STYLES
================================ */
const styles: any = {
  container: {
    padding: "24px",
    background: "linear-gradient(135deg, #21808D 0%, #10b981 100%)",
    minHeight: "100vh",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
  },
  header: {
    textAlign: "center",
    color: "#fff",
    marginBottom: "32px"
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    margin: "0 0 8px 0",
    textShadow: "0 2px 4px rgba(0,0,0,0.1)"
  },
  subtitle: {
    fontSize: "16px",
    margin: 0,
    opacity: 0.9,
    fontWeight: "400"
  },
  filterCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "2px solid #f0f0f0"
  },
  navBtn: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  },
  monthDisplay: {
    flex: 1,
    textAlign: "center",
    padding: "0 20px"
  },
  monthText: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2d3748",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  filters: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap"
  },
  selectGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  select: {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    fontWeight: "500",
    color: "#2d3748",
    background: "#fff",
    cursor: "pointer",
    transition: "border-color 0.2s",
    outline: "none"
  },
  exportBtn: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#fff",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  },
  btnIcon: {
    fontSize: "16px"
  },
  loadingCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "60px",
    textAlign: "center",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
  },
  spinner: {
    width: "50px",
    height: "50px",
    margin: "0 auto 20px",
    border: "4px solid #f3f4f6",
    borderTop: "4px solid #667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  loadingText: {
    color: "#6b7280",
    fontSize: "16px",
    margin: 0
  },
  tableCard: {
    background: "#fff",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    marginBottom: "24px"
  },
  tableHeader: {
    padding: "20px 24px",
    borderBottom: "2px solid #f0f0f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tableHeaderRight: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap"
  },
  tableTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#2d3748"
  },
  badge: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600"
  },
  filterBadge: {
    background: "#f3f4f6",
    color: "#4a5568",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    border: "2px solid #e2e8f0"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    padding: "16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #f0f0f0",
    background: "#f9fafb"
  },
  tr: {
    transition: "background 0.2s"
  },
  td: {
    padding: "16px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "14px",
    color: "#4a5568"
  },
  orderNo: {
    fontWeight: "600",
    color: "#667eea"
  },
  customerInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  customerName: {
    fontWeight: "600",
    color: "#2d3748"
  },
  customerPhone: {
    fontSize: "12px",
    color: "#6b7280"
  },
  paymentBadge: {
    background: "#f3f4f6",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#4a5568"
  },
  dpBadge: {
    background: "#e0e7ff",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#4338ca"
  },
  price: {
    fontWeight: "700",
    color: "#10b981",
    fontSize: "15px"
  },
  date: {
    color: "#6b7280",
    fontSize: "13px"
  },
  emptyState: {
    padding: "60px",
    textAlign: "center"
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px"
  },
  emptyText: {
    fontSize: "16px",
    color: "#6b7280",
    marginBottom: "12px"
  },
  emptyHint: {
    fontSize: "14px",
    color: "#9ca3af",
    marginTop: "8px"
  },
  debugBanner: {
    background: "#fff3cd",
    border: "2px solid #ffc107",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
  },
  debugHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px"
  },
  debugIcon: {
    fontSize: "24px"
  },
  debugTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    color: "#856404"
  },
  debugContent: {
    color: "#856404"
  },
  debugText: {
    margin: "8px 0",
    fontSize: "14px",
    lineHeight: "1.6"
  },
  debugList: {
    margin: "12px 0",
    paddingLeft: "24px",
    fontSize: "14px",
    lineHeight: "1.8"
  },
  code: {
    background: "#f3f4f6",
    padding: "2px 8px",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "13px",
    color: "#e11d48"
  },
  successBanner: {
    background: "#d4edda",
    border: "2px solid #28a745",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
  },
  successIcon: {
    fontSize: "24px"
  },
  successText: {
    margin: "4px 0 0 0",
    fontSize: "13px",
    color: "#155724"
  },
  monthSuggestions: {
    marginTop: "12px",
    padding: "12px",
    background: "#c3e6cb",
    borderRadius: "8px",
    fontSize: "13px"
  },
  monthBadges: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "8px"
  },
  monthBadge: {
    background: "#fff",
    border: "2px solid #28a745",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    color: "#155724",
    transition: "all 0.2s"
  },
  monthCount: {
    marginLeft: "4px",
    color: "#28a745",
    fontWeight: "700"
  },
  quickSelectBtn: {
    background: "#667eea",
    color: "#fff",
    border: "none",
    padding: "4px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    margin: "0 4px",
    transition: "all 0.2s"
  },
  summaryCard: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px"
  },
  summaryItem: {
    background: "#fff",
    borderRadius: "12px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    transition: "transform 0.2s"
  },
  summaryIcon: {
    fontSize: "32px",
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
  },
  summaryLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px"
  },
  summaryValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2d3748"
  }
}

export default OrderReportPage